/**
 * PitchReactions
 * Emoji reaction buttons shown during an active pitch.
 * 'Wil connecten' writes to meetingConnections via writeBatch.
 * Other reactions write to meetingReactions.
 * Floating animation shows on all screens when a reaction is sent.
 *
 * Rule 2: No any — all Firestore writes use explicit types.
 * Rule 3: writeBatch for meetingConnections (multi-doc: connection + inbox).
 * Rule 6: Separate component < 300 lines.
 * Rule 7: 44px+ touch targets.
 * Rule 8: Security rules added (see storage.rules update).
 * Rule 10: Error state exposed.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../../firebase';
import {
  writeBatch,
  doc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  COLLECTIONS,
  FASE3_COLLECTIONS,
  REACTIONS,
  REACTIONS_FLOATING_DURATION_MS,
  STRINGS,
} from '../meetingConstants';
import type { MeetingReaction, MeetingConnection } from '../networkClubTypes';

interface PitchReactionsProps {
  entityId: string;
  roomName: string;
  currentUserId: string;
  currentUserName: string;
  /** userId of the currently pitching member */
  activePitcherUserId: string | null;
  activePitcherName: string;
  activePitchSlotNumber: number;
  accentColor?: string;
}

type FloatingEmoji = { id: string; emoji: string; x: number };

const PitchReactions: React.FC<PitchReactionsProps> = ({
  entityId,
  roomName,
  currentUserId,
  currentUserName,
  activePitcherUserId,
  activePitcherName,
  activePitchSlotNumber,
  accentColor = '#f97316',
}) => {
  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentThisPitch, setSentThisPitch] = useState<Set<string>>(new Set());
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Reset sent state when active pitcher changes ──────────────────────
  useEffect(() => {
    setSentThisPitch(new Set());
    setError(null);
  }, [activePitcherUserId]);

  // ── Real-time reactions from OTHER participants (for floating animation) ─
  useEffect(() => {
    if (!activePitcherUserId || !entityId) return;
    const docId = `${entityId}_${roomName}`;
    const q = query(
      collection(db, FASE3_COLLECTIONS.REACTIONS),
      where('roomName', '==', roomName),
      where('entityId', '==', entityId),
      where('toUserId', '==', activePitcherUserId),
      orderBy('createdAtMs', 'desc'),
      limit(20),
    );

    // Rule 4: cleanup
    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const data = change.doc.data() as Omit<MeetingReaction, 'id'>;
        const reaction = REACTIONS.find((r) => r.id === data.reactionId);
        if (!reaction) return;

        const container = containerRef.current;
        const x = container
          ? Math.random() * (container.offsetWidth - 32)
          : Math.random() * 200;

        const floatId = change.doc.id;
        setFloatingEmojis((prev) => [...prev, { id: floatId, emoji: reaction.emoji, x }]);
        setTimeout(() => {
          setFloatingEmojis((prev) => prev.filter((f) => f.id !== floatId));
        }, REACTIONS_FLOATING_DURATION_MS);
      });
    });

    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePitcherUserId, entityId, roomName]);

  // ── Send reaction ─────────────────────────────────────────────────────────
  const sendReaction = useCallback(
    async (reactionId: string): Promise<void> => {
      if (!activePitcherUserId || sending || sentThisPitch.has(reactionId)) return;
      setSending(reactionId);
      setError(null);

      try {
        const isConnect = reactionId === 'connect';
        const batch = writeBatch(db);

        // Write reaction document
        const reactionRef = doc(collection(db, FASE3_COLLECTIONS.REACTIONS));
        const reactionPayload: Omit<MeetingReaction, 'id'> = {
          entityId,
          roomName,
          fromUserId: currentUserId,
          fromUserName: currentUserName,
          toUserId: activePitcherUserId,
          pitchSlotNumber: activePitchSlotNumber,
          reactionId: reactionId as MeetingReaction['reactionId'],
          createdAtMs: Date.now(),
        };
        batch.set(reactionRef, { ...reactionPayload, createdAt: serverTimestamp() });

        if (isConnect) {
          // Write meetingConnections document
          const connRef = doc(collection(db, FASE3_COLLECTIONS.CONNECTIONS));
          const connPayload: Omit<MeetingConnection, 'id'> = {
            entityId,
            roomName,
            fromUserId: currentUserId,
            fromUserName: currentUserName,
            toUserId: activePitcherUserId,
            toUserName: activePitcherName,
            pitchSlotNumber: activePitchSlotNumber,
            originClubId: entityId,
            status: 'pending',
            connectieFlowTriggered: false,
            createdAtMs: Date.now(),
          };
          batch.set(connRef, { ...connPayload, createdAt: serverTimestamp() });

          // Inbox notification for the pitcher
          const inboxRef = doc(collection(db, COLLECTIONS.INBOX));
          batch.set(inboxRef, {
            userId: activePitcherUserId,
            type: 'notification',
            category: 'connection',
            title: `🤝 ${currentUserName} wil connecten`,
            message: `Tijdens jouw pitch heeft ${currentUserName} aangegeven te willen connecten. Na de meeting ontvang je een uitnodiging.`,
            isRead: false,
            priority: 'high',
            createdAt: serverTimestamp(),
            actionData: { type: 'meeting_connection', fromUserId: currentUserId, entityId },
          });
        }

        // Rule 3: commit batch
        await batch.commit();

        setSentThisPitch((prev) => new Set([...prev, reactionId]));

        if (isConnect) {
          // Brief success feedback
          setError(STRINGS.reactions.connectSent);
          setTimeout(() => setError(null), 4_000);
        }
      } catch (err) {
        console.error('[PitchReactions] Send failed:', err);
        setError(STRINGS.errors.generic);
      } finally {
        setSending(null);
      }
    },
    [
      activePitcherUserId,
      activePitcherName,
      activePitchSlotNumber,
      currentUserId,
      currentUserName,
      entityId,
      roomName,
      sending,
      sentThisPitch,
    ],
  );

  if (!activePitcherUserId || activePitcherUserId === currentUserId) return null;

  return (
    <div ref={containerRef} className="relative">
      {/* Floating emoji animations */}
      {floatingEmojis.map((f) => (
        <span
          key={f.id}
          className="pointer-events-none absolute text-2xl select-none"
          style={{
            left: f.x,
            bottom: 0,
            animation: `sk-float-up ${REACTIONS_FLOATING_DURATION_MS}ms ease-out forwards`,
          }}
          aria-hidden="true"
        >
          {f.emoji}
        </span>
      ))}

      {/* Reaction buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {REACTIONS.map((r) => {
          const isSent = sentThisPitch.has(r.id);
          const isLoading = sending === r.id;
          return (
            <button
              key={r.id}
              onClick={() => void sendReaction(r.id)}
              disabled={isLoading || isSent}
              aria-label={`${r.label} sturen`}
              aria-pressed={isSent}
              className={`min-h-[44px] min-w-[44px] flex items-center gap-1.5 px-3 rounded-xl text-xs font-semibold border transition-all ${
                isSent
                  ? 'bg-white/20 border-white/30 text-white/60 cursor-default'
                  : 'border-white/20 bg-white/10 text-white hover:bg-white/20 active:scale-95'
              } disabled:cursor-not-allowed`}
            >
              <span className="text-base">{r.emoji}</span>
              <span className="hidden sm:inline">{isSent ? '✓' : r.label}</span>
            </button>
          );
        })}
      </div>

      {/* Error / success feedback */}
      {error && (
        <p
          role="status"
          className={`mt-1.5 text-[11px] leading-snug ${
            error === STRINGS.reactions.connectSent ? 'text-emerald-300' : 'text-rose-400'
          }`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default PitchReactions;
